import { IFileOptions, IFileParameterSet, IFileService } from "./PA.File.IF";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import axios from "axios";
import { BehaviorSubject } from "rxjs";

export class PAFile implements IFileService {

    private _OnDownloadCompleted: BehaviorSubject<IFileService>;

    constructor() {
        this._OnDownloadCompleted = new BehaviorSubject<IFileService>(this);
    }

    get OnDownloadCompleted(): BehaviorSubject<IFileService> {
        return this._OnDownloadCompleted;
    }

    downloadFile(wbFileId): Promise<string> {
        const self = this;

        return new Promise<string>((resolve, reject) => {

            let sEndPoint = <string>(PAEnvironment.getValue(PAEnvironment.Network_URL_Base, "/"));
            if (sEndPoint.endsWith("/"))
                sEndPoint += "download";
            else
                sEndPoint += "/download";

            // Fetch as a blob instead of navigating the whole page to the download URL: a
            // navigation on a failed download (404/500 with a plain-text body) would blow away
            // the SPA. Fetching lets us tell success from failure and show the real error.
            axios.get(sEndPoint, {
                params: { params: wbFileId },
                responseType: "blob"
            })
                .then((resp: any) => {
                    let sFileName = self._getFileNameFromDisposition(resp.headers["content-disposition"]) || "download";
                    let sUrl = window.URL.createObjectURL(resp.data);
                    let oLink = document.createElement("a");
                    oLink.href = sUrl;
                    oLink.download = sFileName;
                    document.body.appendChild(oLink);
                    oLink.click();
                    document.body.removeChild(oLink);
                    window.URL.revokeObjectURL(sUrl);
                    resolve("file downloaded");
                })
                .catch((err: any) => {
                    self._rejectDownloadError(err, reject);
                });
        });
    }

    private _getFileNameFromDisposition(sDisposition: string): string {
        if (!sDisposition) return undefined;
        let m = /filename="?([^";]+)"?/.exec(sDisposition);
        return m ? m[1] : undefined;
    }

    private _rejectDownloadError(err: any, reject: (reason: string) => void): void {
        // With responseType: "blob", an error body (our plain-text "Error: ..." message) arrives
        // as a Blob too, so it has to be read asynchronously before it can be used as the reject reason.
        let oBlob: Blob = err && err.response && err.response.data instanceof Blob ? err.response.data : undefined;
        if (oBlob) {
            let oReader = new FileReader();
            oReader.onload = () => reject(String(oReader.result));
            oReader.onerror = () => reject("Unable to download the file.");
            oReader.readAsText(oBlob);
        } else {
            reject("Unable to download the file. " + (err && err.message ? err.message : String(err)));
        }
    }

    uploadFile(aFile: File, aFileParamSet: IFileParameterSet, aFileOptions: IFileOptions): Promise<string> {
        const self = this;
        let formData = new FormData();
        formData.append("file", aFile, aFile.name);
        let params = {
            method: "uploadFile",
            fileOptions: aFileOptions,
            fileParamSet: aFileParamSet
        };
        return new Promise<string>((resolve, reject) => {
            let sEndPoint = <string>(PAEnvironment.getValue(PAEnvironment.Network_URL_Base, "/"));
            if (sEndPoint.endsWith("/"))
                sEndPoint += "upload";
            else
                sEndPoint += "/upload";
            axios.post(sEndPoint, formData, {
                params,
                headers: { "CONTENT-TYPE": "multipart/form-data" }
            })
                .then((resp: any) => {
                    resolve("file uploaded");
                })
                .catch((err: any) => {
                    // The server sends a plain-text error message in the response body on failure
                    // (e.g. "Error: Unable to store attachment file"); axios rejects the whole
                    // request on non-2xx, so that message lives on err.response.data.
                    let sMessage = err && err.response && err.response.data
                        ? String(err.response.data)
                        : "Unable to upload the file. " + (err && err.message ? err.message : String(err));
                    reject(sMessage);
                });

        });
    }
}